<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { LoaderOverlay } from '@shared/ui/loaders'
import { AppDatePicker, NativeSelect } from '@shared/ui/fields'
import { CardAdmin } from '@shared/ui/cards'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useLessonEditStore } from '../../model/lessons/edit/store'
import { useLessonEditSingle } from '../../model/lessons/lesson-edit-single.hook'
import { useLessonEditTitle } from '../../model/lessons/lesson-edit-title.hook'
import LessonFormLayout from './LessonFormLayout.vue'
import LessonTutorBlock from './LessonTutorBlock.vue'
import LessonEditActions from './LessonEditActions.vue'
import ModalLessonEditTutor from './modals/ModalLessonEditTutor.vue'

defineOptions({
  name: 'LessonEditSingle',
})

const store = useLessonEditStore()

const {
  refCanEdit,
  refDate,
  refStartOptions,
  refStartTime,
  refDurationOptions,
  refDuration,
  refStatusOptions,
  refStatus,
  refIsReasonShown,
  refReasonCategoryOptions,
  refReasonCategory,
  refReasonOptions,
  refReason,
  refIsTimeLocked,
  refTutorName,
  refTutorScheduleUrl,
  refIsTutorScheduleDisabled,
  refChangeTutorText,
  refCanChangeTutor,
  refIsSaving,
  refLessonsUrl,
  setDate,
  setStartTime,
  setDuration,
  setStatus,
  setReasonCategory,
  setReason,
  openTutorModal,
  submit,
} = useLessonEditSingle()

const { refTitle } = useLessonEditTitle()
</script>

<template>
  <CardAdmin
    :title="refTitle"
    class="lesson-edit-single"
    fullwidth
  >
    <template #header>
      <div class="lesson-edit-single__controls">
        <LessonEditActions />
        <BtnSecondary
          text="Go back"
          color="black"
          :to="refLessonsUrl"
        />
        <BtnPrimary
          v-if="refCanEdit"
          text="Save"
          color="emerald"
          :disabled="refIsSaving"
          @click="submit"
        />
      </div>
    </template>
    <LessonFormLayout>
      <template #main>
        <div class="lesson-edit-single__form">
          <AppText name="body1-medium">
            Lesson details
          </AppText>
          <div class="lesson-edit-single__grid">
            <AppDatePicker
              class="lesson-edit-single__date"
              label="Date"
              :value="refDate"
              :disabled="refIsSaving || refIsTimeLocked"
              @change="setDate"
            />
            <div class="lesson-edit-single__time">
              <NativeSelect
                class="lesson-edit-single__slot"
                name="time_start"
                label="Time"
                :options="refStartOptions"
                :value="refStartTime"
                :disabled="refIsSaving || refIsTimeLocked"
                @change="setStartTime"
              />
              <AppText
                class="lesson-edit-single__separator"
                name="body2-medium"
                color="slate-900"
              >
                to
              </AppText>
              <NativeSelect
                class="lesson-edit-single__slot"
                name="time_end"
                :options="refDurationOptions"
                :value="refDuration"
                :disabled="refIsSaving || refIsTimeLocked"
                @change="setDuration"
              />
            </div>
            <NativeSelect
              class="lesson-edit-single__status"
              name="lesson_status"
              label="Status"
              :options="refStatusOptions"
              :value="refStatus"
              :disabled="refIsSaving"
              required
              @change="setStatus"
            />
            <template v-if="refIsReasonShown">
              <NativeSelect
                class="lesson-edit-single__reason-category"
                name="reason_category"
                label="Reason category"
                :options="refReasonCategoryOptions"
                :value="refReasonCategory"
                :disabled="refIsSaving"
                required
                @change="setReasonCategory"
              />
              <NativeSelect
                class="lesson-edit-single__reason"
                name="reason"
                label="Reason"
                :options="refReasonOptions"
                :value="refReason"
                :disabled="refIsSaving"
                required
                @change="setReason"
              />
            </template>
          </div>
        </div>
      </template>
      <template #aside>
        <LessonTutorBlock
          :name="refTutorName"
          :schedule-url="refTutorScheduleUrl"
          :schedule-disabled="refIsTutorScheduleDisabled"
          :change-text="refChangeTutorText"
          :can-change="refCanChangeTutor"
          @change="openTutorModal"
        />
      </template>
    </LessonFormLayout>
    <LoaderOverlay v-if="refIsSaving && !store.isTutorModalOpen" />
    <ModalLessonEditTutor v-if="store.isTutorModalOpen" />
  </CardAdmin>
</template>

<style scoped lang="scss" src="./LessonEditSingle.scss" />
